export class SeriesProductGalleryController {
  constructor({ count = 0, index = 0 } = {}) {
    this.count = Math.min(3, Math.max(0, Math.trunc(Number(count) || 0)));
    this.index = 0;
    this.goTo(index);
  }

  goTo(index) {
    if (!this.count) {
      this.index = 0;
      return this.index;
    }
    this.index = Math.min(this.count - 1, Math.max(0, Math.trunc(Number(index) || 0)));
    return this.index;
  }

  next() {
    return this.goTo(this.index + 1);
  }

  previous() {
    return this.goTo(this.index - 1);
  }

  atStart() {
    return this.index <= 0;
  }

  atEnd() {
    return this.count <= 1 || this.index >= this.count - 1;
  }
}

export class LSSeriesProductGallery extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.track = this.querySelector('[data-ls-series-gallery-track]');
    this.slides = [...this.querySelectorAll('[data-ls-series-gallery-slide]')].slice(0, 3);
    this.dots = [...this.querySelectorAll('[data-ls-series-gallery-dot]')].slice(0, 3);
    this.previousButton = this.querySelector('[data-ls-series-gallery-previous]');
    this.nextButton = this.querySelector('[data-ls-series-gallery-next]');
    this.liveRegion = this.querySelector('[data-ls-series-gallery-live]');
    this.motionQuery = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)');
    this.abortController = new AbortController();
    this.scrollFrame = null;
    const { signal } = this.abortController;

    this.controller = new SeriesProductGalleryController({
      count: this.slides.length,
      index: Number(this.dataset.currentIndex) || 0,
    });

    this.previousButton?.addEventListener('click', () => this.navigate(this.controller.previous()), { signal });
    this.nextButton?.addEventListener('click', () => this.navigate(this.controller.next()), { signal });
    this.track?.addEventListener('scroll', () => this.onScroll(), { signal, passive: true });
    this.track?.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    globalThis.addEventListener?.('resize', () => this.navigate(this.controller.index, { announce: false, behavior: 'auto' }), { signal });
    this.resizeObserver = globalThis.ResizeObserver
      ? new ResizeObserver(() => this.navigate(this.controller.index, { announce: false, behavior: 'auto' }))
      : null;
    if (this.track) this.resizeObserver?.observe(this.track);

    this.setAttribute('data-ready', '');
    this.syncUi({ announce: false });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    if (this.scrollFrame !== null) globalThis.cancelAnimationFrame?.(this.scrollFrame);
    this.controller = null;
  }

  navigate(index, { announce = true, behavior = 'smooth' } = {}) {
    if (!this.controller) return;
    this.controller.goTo(index);
    const target = this.slides[this.controller.index];
    if (this.track && target) {
      this.track.scrollTo({
        left: target.offsetLeft,
        behavior: this.motionQuery?.matches ? 'auto' : behavior,
      });
    }
    this.syncUi({ announce });
  }

  onScroll() {
    if (!this.track || this.slides.length < 2) return;
    if (this.scrollFrame !== null) globalThis.cancelAnimationFrame?.(this.scrollFrame);
    this.scrollFrame = globalThis.requestAnimationFrame?.(() => {
      this.scrollFrame = null;
      const currentLeft = this.track.scrollLeft;
      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;
      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - currentLeft);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });
      this.controller.goTo(nearestIndex);
      this.syncUi({ announce: false });
    }) ?? null;
  }

  onKeydown(event) {
    if (!this.controller || this.controller.count < 2) return;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.navigate(this.controller.previous());
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.navigate(this.controller.next());
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.navigate(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      this.navigate(this.controller.count - 1);
    }
  }

  syncUi({ announce = true } = {}) {
    if (!this.controller) return;
    this.dataset.currentIndex = String(this.controller.index);
    this.dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === this.controller.index)));
    if (this.previousButton) {
      this.previousButton.hidden = this.controller.count < 2 || this.controller.atStart();
      this.previousButton.disabled = this.controller.atStart();
    }
    if (this.nextButton) {
      this.nextButton.hidden = this.controller.count < 2 || this.controller.atEnd();
      this.nextButton.disabled = this.controller.atEnd();
    }
    if (announce && this.liveRegion) {
      this.liveRegion.textContent = `Product image ${this.controller.index + 1} of ${this.controller.count}`;
    }
  }
}

if (!customElements.get('ls-series-product-gallery')) {
  customElements.define('ls-series-product-gallery', LSSeriesProductGallery);
}
